import AsyncRequestService from '@/modules/core/api/services/AsyncRequestService';
import { AxiosResponseType } from '@/modules/core/app/constants/AxiosResponseType';
import { getters } from '@/modules/core/app/helpers/store';
import { BaseModel } from '@/modules/core/app/models/abstracts/BaseModel';
import { GenericModel } from '@/modules/core/app/models/GenericModel';
import { BaseResource } from '@/modules/core/app/resources/BaseResource';

export class BaseAsyncResource extends BaseResource {
  async getData<T extends BaseModel>(
    url: string,
    model: typeof GenericModel = GenericModel,
    params: Params = {},
    headers: Params = {}
  ): Promise<T> {
    const asyncRequestId: string = (await this.getDataRequest(url, model, params, headers))
      ?.asyncRequestId;

    const promise: Promise<T> = this.getResponsePromise<T>(asyncRequestId, AxiosResponseType.JSON);

    const result: unknown = await promise;

    return this.parsePayload(result, model, params);
  }

  async getBlob(url: string, config: Params = {}): Promise<Blob> {
    const { params, headers } = config;
    const asyncRequestId: string = (await this.getDataRequest(url, GenericModel, params, headers))
      ?.asyncRequestId;

    const promise: Promise<Blob> = this.getResponsePromise<Blob>(
      asyncRequestId,
      AxiosResponseType.BLOB
    );

    const result: unknown = await promise;

    return result as Blob;
  }

  async postData<T extends BaseModel>(
    url: string,
    data: unknown,
    config: Params = {},
    responseType: AxiosResponseType = AxiosResponseType.JSON
  ): Promise<unknown> {
    const asyncRequestId: string = (await super.postData(url, data, config))?.asyncRequestId;

    return this.getResponsePromise<T>(asyncRequestId, responseType);
  }

  private getResponsePromise<T>(
    asyncRequestId: string,
    responseType: AxiosResponseType
  ): Promise<T> {
    if (getters.auth.getUser()) {
      return AsyncRequestService.addPendingSocketRequest(
        asyncRequestId,
        responseType
      ) as Promise<T>;
    }

    return AsyncRequestService.addPollingAsyncRequest(asyncRequestId, responseType) as Promise<T>;
  }
}
